import type { PluginResource, PluginSummary, ServerSummary } from '@comficonf/shared';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';
import type { ReactNode, RefObject } from 'react';
import { useId, useLayoutEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router';
import { StatusDot } from '@/components/status-dot';
import { Skeleton } from '@/components/ui/skeleton';
import { useLoadError } from '@/components/use-load-error';
import { pluginsQuery, serversQuery } from '@/lib/api/queries';
import { cn } from '@/lib/utils';
import {
  SHELL_SERVERS_LINK_ID,
  treeItemClasses,
  treeNoteClasses,
  treeRetryClasses,
  treeToggleClasses,
} from './shell-classes';
import type { ShellLocation } from './shell-location';
import { currentResourceId, shellLocation } from './shell-location';
import { useFocusFallback } from './use-focus-fallback';

/** Selector of the tree's scroll container. */
const SCROLLER = '[data-tree-scroller]';

/** How a tree link relates to the current location. */
type LocationMark = 'current' | 'ancestor' | undefined;

/** A list item that moves focus to `fallback()` when it disappears while holding focus. */
function TreeItem({
  fallback,
  children,
}: {
  fallback: () => HTMLElement | null;
  children: ReactNode;
}) {
  const item = useRef<HTMLLIElement>(null);
  useFocusFallback(item, fallback);
  return <li ref={item}>{children}</li>;
}

function serversLink(): HTMLElement | null {
  return document.getElementById(SHELL_SERVERS_LINK_ID);
}

/**
 * Scrolls the tree's scroll container by the least amount that shows `link` whole. Focus does not move, and nothing
 * scrolls while the tree is not displayed (all its boxes are empty then).
 */
function revealInTree(link: HTMLElement) {
  const scroller = link.closest<HTMLElement>(SCROLLER);
  if (!scroller) return;
  const box = link.getBoundingClientRect();
  const view = scroller.getBoundingClientRect();
  if (box.top < view.top) scroller.scrollTop -= view.top - box.top;
  else if (box.bottom > view.bottom) scroller.scrollTop += box.bottom - view.bottom;
}

/**
 * Brings `ref`'s link into the tree's view when it marks the current location: on mount, when the mark changes and
 * on each navigation (`locationKey`) while it is marked. An ancestor link is revealed only while the tree has no
 * current link, so it never scrolls the current link away.
 */
function useRevealLocation(
  ref: RefObject<HTMLElement | null>,
  mark: LocationMark,
  locationKey: string,
) {
  useLayoutEffect(() => {
    const link = ref.current;
    if (!mark || !link) return;
    if (mark === 'ancestor' && link.closest(SCROLLER)?.querySelector('[aria-current="page"]')) {
      return;
    }
    revealInTree(link);
  }, [ref, mark, locationKey]);
}

/**
 * The paired servers with their live status, as nested lists of links. Each server has a disclosure button for its
 * plugins; plugins are requested only for expanded servers that are online. The current server is expanded on
 * arrival, other servers on their toggle, and a server opened with its toggle stays open on other pages. A plugin with
 * two or more settings pages lists them below it. The tree scrolls to the link of the current location on arrival.
 */
export function ServerTree() {
  const location = useLocation();
  const here = shellLocation(location.pathname, location.search);
  const locationKey = location.pathname + location.search;
  const servers = useQuery(serversQuery());
  const loadError = useLoadError(servers);

  const [overrides, setOverrides] = useState<Readonly<Record<string, boolean>>>({});
  const [arrivedAt, setArrivedAt] = useState(here.serverId);
  if (arrivedAt !== here.serverId) {
    setArrivedAt(here.serverId);
    if (here.serverId !== undefined && overrides[here.serverId] === false) {
      const { [here.serverId]: _dropped, ...rest } = overrides;
      setOverrides(rest);
    }
  }
  const isExpanded = (id: string) => overrides[id] ?? id === here.serverId;
  const onToggle = (id: string, open: boolean) =>
    setOverrides((current) => ({ ...current, [id]: open }));

  let state: ReactNode;
  if (servers.data) {
    state =
      servers.data.length === 0 ? (
        <p className={treeNoteClasses}>Paired servers appear here.</p>
      ) : (
        <ul role="list" className="space-y-px">
          {servers.data.map((server) => (
            <ServerNode
              key={server.id}
              server={server}
              here={here}
              locationKey={locationKey}
              expanded={isExpanded(server.id)}
              onToggle={onToggle}
            />
          ))}
        </ul>
      );
  } else if (loadError) {
    state = (
      <>
        <p className={treeNoteClasses}>Server list unavailable.</p>
        <RetryButton fetching={servers.isFetching} onRetry={() => void servers.refetch()} />
      </>
    );
  } else {
    state = (
      <>
        <Skeleton className="mx-2 my-1 h-5 bg-chrome-2" />
        <Skeleton className="mx-2 my-1 h-5 bg-chrome-2" />
      </>
    );
  }

  return (
    <nav
      aria-label="Servers and plugins"
      className="hidden min-h-0 lg:mt-5 lg:flex lg:flex-1 lg:flex-col"
    >
      <p
        aria-hidden
        className="px-2 pb-1.5 text-xs font-semibold uppercase tracking-wider text-chrome-muted"
      >
        Servers
      </p>
      {/* The containing block of its absolutely positioned descendants, so they scroll and clip with the tree. */}
      <div
        data-tree-scroller=""
        className="relative min-h-0 flex-1 overflow-y-auto pb-2 [scrollbar-color:var(--color-chrome-border-strong)_transparent] [scrollbar-width:thin]"
      >
        {state}
      </div>
    </nav>
  );
}

/** Retry of a failed request. It stays enabled while the retry runs, so it keeps focus. */
function RetryButton({
  fetching,
  onRetry,
  label,
}: {
  fetching: boolean;
  onRetry: () => void;
  label?: string;
}) {
  return (
    <button
      type="button"
      className={treeRetryClasses}
      aria-label={label}
      aria-busy={fetching || undefined}
      onClick={() => {
        if (!fetching) onRetry();
      }}
    >
      Retry
    </button>
  );
}

function ServerNode({
  server,
  here,
  locationKey,
  expanded,
  onToggle,
}: {
  server: ServerSummary;
  here: ShellLocation;
  locationKey: string;
  expanded: boolean;
  onToggle: (id: string, open: boolean) => void;
}) {
  const childrenId = useId();
  const toggle = useRef<HTMLButtonElement>(null);
  const link = useRef<HTMLAnchorElement>(null);
  const online = server.status === 'online';
  const isHere = here.serverId === server.id;
  useRevealLocation(
    link,
    isHere ? (here.serverPage ? 'current' : 'ancestor') : undefined,
    locationKey,
  );
  return (
    <TreeItem fallback={serversLink}>
      <div className="flex min-w-0 items-center gap-0.5">
        <button
          ref={toggle}
          type="button"
          className={treeToggleClasses}
          aria-expanded={expanded}
          aria-controls={childrenId}
          aria-label={`${server.displayName} plugins`}
          onClick={() => onToggle(server.id, !expanded)}
        >
          <ChevronRight
            aria-hidden
            className={cn('transition-transform duration-150', expanded && 'rotate-90')}
          />
        </button>
        {/*
         * The status is in aria-label, not in visually hidden text: browsers put a space before the text of an
         * absolutely positioned or block-level child when they compute a name from content.
         */}
        <NavLink
          ref={link}
          to={`/servers/${server.id}`}
          end
          aria-label={`${server.displayName}, ${online ? 'online' : 'offline'}`}
          data-ancestor={isHere && !here.serverPage ? '' : undefined}
          className={cn(treeItemClasses, 'text-chrome-text')}
        >
          <StatusDot
            status={server.status}
            className={online ? 'bg-chrome-success' : 'border-chrome-muted'}
          />
          <span className="min-w-0 flex-1 truncate" title={server.displayName}>
            {server.displayName}
          </span>
        </NavLink>
      </div>
      <div
        id={childrenId}
        hidden={!expanded}
        className="ml-3 border-l border-chrome-border-strong pl-1.5"
      >
        {expanded ? (
          <ServerPlugins server={server} here={here} locationKey={locationKey} toggle={toggle} />
        ) : null}
      </div>
    </TreeItem>
  );
}

function ServerPlugins({
  server,
  here,
  locationKey,
  toggle,
}: {
  server: ServerSummary;
  here: ShellLocation;
  locationKey: string;
  toggle: RefObject<HTMLButtonElement | null>;
}) {
  const online = server.status === 'online';
  const plugins = useQuery({ ...pluginsQuery(server.id), enabled: online });
  const loadError = useLoadError(plugins);
  const toToggle = () => toggle.current;

  if (plugins.data) {
    if (plugins.data.length === 0) {
      return <p className={treeNoteClasses}>No plugins share settings yet.</p>;
    }
    return (
      <ul role="list" className="space-y-px py-0.5">
        {plugins.data.map((plugin) => (
          <PluginNode
            key={plugin.slug}
            server={server}
            plugin={plugin}
            here={here}
            locationKey={locationKey}
            fallback={toToggle}
          />
        ))}
      </ul>
    );
  }
  if (!online) {
    return <p className={treeNoteClasses}>Offline. Plugins appear when it reconnects.</p>;
  }
  if (loadError) {
    return (
      <>
        <p className={treeNoteClasses}>Plugins unavailable.</p>
        <RetryButton
          label={`Retry plugins of ${server.displayName}`}
          fetching={plugins.isFetching}
          onRetry={() => void plugins.refetch()}
        />
      </>
    );
  }
  return (
    <>
      <Skeleton className="mx-2 my-1 h-4 bg-chrome-2" />
      <Skeleton className="mx-2 my-1 h-4 bg-chrome-2" />
    </>
  );
}

function PluginNode({
  server,
  plugin,
  here,
  locationKey,
  fallback,
}: {
  server: ServerSummary;
  plugin: PluginSummary;
  here: ShellLocation;
  locationKey: string;
  fallback: () => HTMLElement | null;
}) {
  const link = useRef<HTMLAnchorElement>(null);
  const pathname = `/servers/${server.id}/plugins/${plugin.slug}`;
  const isCurrentPlugin = here.serverId === server.id && here.pluginSlug === plugin.slug;
  const current = isCurrentPlugin ? currentResourceId(plugin, here.resource) : undefined;
  const multi = plugin.resources.length >= 2;
  const isCurrent = !multi && current !== undefined;
  useRevealLocation(
    link,
    isCurrent ? 'current' : isCurrentPlugin ? 'ancestor' : undefined,
    locationKey,
  );
  return (
    <TreeItem fallback={fallback}>
      {/* The version wraps below a name that leaves it no room, so the name keeps the row's width. */}
      <Link
        ref={link}
        to={pathname}
        className={cn(treeItemClasses, 'flex-wrap gap-y-0')}
        aria-current={isCurrent ? 'page' : undefined}
        data-ancestor={multi && isCurrentPlugin ? '' : undefined}
      >
        <span className="min-w-0 flex-auto truncate" title={plugin.name}>
          {plugin.name}
        </span>{' '}
        {plugin.version ? (
          <span
            className="max-w-full truncate font-mono text-xs text-chrome-muted"
            title={plugin.version}
          >
            {plugin.version}
          </span>
        ) : null}
      </Link>
      {multi ? (
        <ul role="list" className="ml-3 space-y-px border-l border-chrome-border-strong pl-1.5">
          {plugin.resources.map((resource) => (
            <ResourceNode
              key={resource.id}
              pathname={pathname}
              resource={resource}
              current={current === resource.id}
              locationKey={locationKey}
              fallback={fallback}
            />
          ))}
        </ul>
      ) : null}
    </TreeItem>
  );
}

function ResourceNode({
  pathname,
  resource,
  current,
  locationKey,
  fallback,
}: {
  pathname: string;
  resource: PluginResource;
  current: boolean;
  locationKey: string;
  fallback: () => HTMLElement | null;
}) {
  const link = useRef<HTMLAnchorElement>(null);
  useRevealLocation(link, current ? 'current' : undefined, locationKey);
  return (
    <TreeItem fallback={fallback}>
      <Link
        ref={link}
        to={{
          pathname,
          search: `?${new URLSearchParams({ resource: resource.id }).toString()}`,
        }}
        className={treeItemClasses}
        aria-current={current ? 'page' : undefined}
      >
        <span className="min-w-0 truncate" title={resource.displayName}>
          {resource.displayName}
        </span>
      </Link>
    </TreeItem>
  );
}
